import api from './api';
import { buildQueryParams } from './utils/query-params';
import type {
  ApiResponse,
  PaginatedResponse,
  Quote,
  FlightCreditDirection,
  QuoteFilters,
  CreateQuoteData,
  UpdateQuoteData,
  QuoteStats,
  QuoteStatus,
  SendEmailPreview,
  SendWhatsAppPreview,
  QuoteInvoiceProjection,
} from '@/types';

// Get quotes with filtering and pagination
export const getQuotes = async (filters: QuoteFilters = {}) => {
  const query = buildQueryParams(filters);
  const response = await api.get<PaginatedResponse<Quote>>(`/quotes?${query}`);
  return response.data;
};

// Get single quote
export const getQuote = async (id: string) => {
  const response = await api.get<ApiResponse<Quote>>(`/quotes/${id}`);
  return response.data.data;
};

// ArtDent 260918 — the quote's invoice projection, from the one server-side
// mapping the quote→invoice sync also uses.
export const getQuoteInvoiceProjection = async (id: string) => {
  const response = await api.get<ApiResponse<QuoteInvoiceProjection>>(
    `/quotes/${id}/invoice-projection`,
  );
  return response.data.data;
};

// Create quote (basic)
export const createQuote = async (data: CreateQuoteData) => {
  const response = await api.post<ApiResponse<Quote>>('/quotes', data);
  return response.data.data;
};

// Create enhanced quote with line items
// Phase 49 Pitfall 7 fix: enhanced types were imported from @/types but never
// defined there. Use existing CreateQuoteData + Quote canonical types.
export const createEnhancedQuote = async (data: CreateQuoteData) => {
  const response = await api.post<ApiResponse<Quote>>('/quotes', data);
  return response.data.data;
};

// Smile imaging upload (multipart) — called after the quote is created.
export const uploadQuoteMedia = async (
  quoteId: string,
  files: { panoramic?: File | null; images?: File[] },
) => {
  const form = new FormData();
  if (files.panoramic) {
    form.append('panoramic', files.panoramic);
  }
  (files.images ?? []).forEach((f) => form.append('quote_images[]', f));
  const response = await api.post(`/quotes/${quoteId}/media`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// Update quote
// ArtDent 260918 — on quote_invoice_sync_enabled the backend re-projects the
// edited quote onto its live invoice and says what happened in
// `meta.invoice_sync` (synced / none / skipped:fiscalized|multiple).
export interface QuoteInvoiceSyncOutcome {
  status: 'synced' | 'none' | 'skipped';
  reason: 'fiscalized' | 'multiple' | null;
  invoice_id: string | null;
  invoice_number: string | null;
  released_to_deposit: number;
}

export const updateQuote = async (id: string, data: UpdateQuoteData) => {
  const response = await api.put<
    ApiResponse<Quote> & { meta?: { invoice_sync?: QuoteInvoiceSyncOutcome } }
  >(`/quotes/${id}`, data);
  return {
    quote: response.data.data,
    invoiceSync: response.data.meta?.invoice_sync ?? null,
  };
};

// Delete quote
export const deleteQuote = async (id: string) => {
  await api.delete(`/quotes/${id}`);
};

// Update quote status
export const updateQuoteStatus = async (id: string, status: QuoteStatus) => {
  const response = await api.patch<ApiResponse<Quote>>(`/quotes/${id}/status`, { status });
  return response.data.data;
};

/**
 * ArtDent 260922 — back to draft from any later status (quotes.reopen).
 * 422 with `code: "invoice_attached"` when a live invoice hangs off the quote;
 * the backend refuses rather than orphan the bill.
 */
export const reopenQuote = async (id: string) => {
  const response = await api.post<ApiResponse<Quote>>(`/quotes/${id}/reopen`);
  return response.data.data;
};

// Send quote (mark as sent)
export const sendQuote = async (id: string) => {
  const response = await api.post<ApiResponse<Quote>>(`/quotes/${id}/send`);
  return response.data.data;
};

// Duplicate quote
export const duplicateQuote = async (id: string) => {
  const response = await api.post<ApiResponse<Quote>>(`/quotes/${id}/duplicate`);
  return response.data.data;
};

// Phase 70 D-09: propose a quote revision tied to a treatment.
// Backend snapshots the parent + creates a NEW versioned quote with
// parent_quote_id + revision_of_treatment_id set. On future acceptance
// (PATCH /quotes/{new}/status with accepted), the treatment's current_quote_id
// advances automatically via the Quote::onAccepted dispatcher.
export interface ProposeQuoteRevisionPayload {
  lead_id: string;
  source_treatment_id: string;
  language?: string;
  currency?: string;
  title?: string;
  treatment_notes?: string;
  items: Array<{
    catalog_item_id?: string | null;
    arch?: "upper" | "lower" | null;
    name: { en: string; [key: string]: string | undefined };
    // Accept both legacy i18n {en?, it?, al?} description shape and plain string.
    description?: string | { en?: string; it?: string; al?: string } | null;
    quantity?: number;
    unit_price?: number;
    order?: number;
  }>;
  discount_percent?: number;
  discount_type?: 'percent' | 'fixed';
  discount_amount?: number;
  // Radiance Basic credit lines + terms. Omitted = inherit the parent's value;
  // present (null included) = override it.
  flight_credit_amount?: number | null;
  flight_credit_direction?: FlightCreditDirection;
  tax_credit_percent?: number | null;
  tax_credit_amount?: number | null;
  terms_set?: string | null;
  visits?: Array<{
    label: string;
    days: number;
    mode?: 'percent' | 'fixed';
    percent?: number;
    amount?: number;
  }>;
  valid_days?: number;
  deposit_type?: 'percent' | 'fixed';
  deposit_percent?: number;
  deposit_amount?: number;
  patient_info?: Record<string, unknown>;
}

export const createQuoteRevision = async (
  parentQuoteId: string,
  payload: ProposeQuoteRevisionPayload,
) => {
  const response = await api.post<ApiResponse<Quote>>(
    `/quotes/${parentQuoteId}/propose-revision`,
    payload,
  );
  return response.data.data;
};

// Get recent quotes
export const getRecentQuotes = async (limit = 10) => {
  const response = await api.get<{ data: Quote[] }>(`/quotes/recent?limit=${limit}`);
  return response.data.data;
};

// Get quote stats
export const getQuoteStats = async () => {
  const response = await api.get<QuoteStats>('/quotes/stats');
  return response.data;
};

// Get quotes for a specific lead
export const getQuotesForLead = async (leadId: string) => {
  const response = await api.get<{ data: Quote[] }>(`/leads/${leadId}/quotes`);
  return response.data.data;
};

// Get PDF preview URL
export const getQuotePdfPreviewUrl = (quoteId: string, language?: string) => {
  const params = language ? `?language=${language}` : '';
  return `${api.defaults.baseURL}/quotes/${quoteId}/pdf/preview${params}`;
};

// Download PDF
export const downloadQuotePdf = async (quoteId: string, options?: {
  language?: string;
  include_doctors?: boolean;
  include_testimonials?: boolean;
  include_guarantees?: boolean;
  include_journey?: boolean;
  include_comparison?: boolean;
}) => {
  const params = new URLSearchParams();
  if (options) {
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, String(value));
      }
    });
  }
  const queryString = params.toString();
  const url = `/quotes/${quoteId}/pdf${queryString ? `?${queryString}` : ''}`;

  const response = await api.get(url, {
    responseType: 'blob',
  });

  // Create download link
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `quote-${quoteId}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(downloadUrl);
};


// Phase 58: i18n object as stored in Setting('clinic_branding').
// Frontend reads raw 3-lang objects so it can do the D-14 third-hop
// fallback to translations const when both lang and en values are
// missing for a given field.
export type LangString = {
  en: string;
  it: string;
  sq: string;
};

// Public quote data type
export interface PublicQuoteData {
  /**
   * The partner who referred this patient, when there is one and they carry
   * branding (ArtDent 260904). NULL for a direct patient — which is the case
   * that must keep rendering exactly as it always has.
   *
   * `logo_url` points at a token-scoped API route, not a static file: the image
   * lives in the backend's public/, and nginx sends everything except /api to
   * Next.js, so the API is the only path a browser has to it.
   */
  affiliator?: {
    name: string;
    logo_url: string;
  } | null;
  quote: {
    quote_number: string;
    language: string;
    currency: string;
    created_at: string;
    valid_until: string | null;
    /** The stored validity in days, for the closing line (null on quotes from before it was kept). */
    valid_days?: number | null;
    status: string;
    /** Which closing terms radiance-basic renders. Server sends 'standard' when unset. */
    terms_set?: 'standard' | 'aligners' | null;
    // Smile theme "Consulente Medico" line (from quote.patient_info.consultant).
    consultant?: string | null;
    // The staff member the patient deals with — the quote's author, else the
    // lead's assignee. Radiance Preventivo cover "Assistente" (261002).
    prepared_by?: string | null;
    // Structured treatment plan ("Piano Cure") for the Smile theme.
    treatment_phases?: {
      treatment?: { title?: string; description?: string };
      phases?: Array<{ title?: string; description?: string; details?: Array<{ title?: string; description?: string }> }>;
    } | null;
  };
  patient: {
    name: string;
    full_name: string | null;
    country: string | null;
    /** The quote's typed phone, else the lead's (dentadent-preventivo "Contatto"). */
    phone?: string | null;
    date_of_birth: string | null;
    // Smile theme patient-info page (from quote.patient_info); optional/legacy.
    age?: string | number | null;
    cartel_nr?: string | null;
    medications?: string | null;
    diseases?: string | null;
    diagnose?: string | null;
    // Smile imaging.
    panoramic_legend?: string[] | null;
    panoramic_image?: Array<{ url: string }>;
    quote_images?: Array<{ id: string | number; url: string }>;
  };
  items: {
    upper_arch: Array<{
      id: string;
      name: string | { en?: string; it?: string; al?: string };
      description: string | { en?: string; it?: string; al?: string } | null;
      // i18n object in practice, not a string: the resource ships the category's
      // whole name map. Typed as both because legacy rows can carry a bare string.
      category: string | { en?: string; it?: string; al?: string; es?: string } | null;
      position?: string | null;
      // FDI teeth this line treats (EKDent 260905). Printed in place of the
      // arch by designs that opt in; always sent.
      teeth?: number[];
      /** ArtDent 260924 — treatment phase; null on a phase-less quote. */
      phase?: number | null;
      /** Phase 89 — the doctor for this line; omit where the flag is off. */
      doctor_id?: number | null;
      quantity: number;
      unit_price: number;
      // Free-text cost label ("Included", "Gratis") printed INSTEAD of the
      // amount when set. Sent by QuotePublicController for ALL THREE groups —
      // it was declared on `general` alone, so a design reading it off an arch
      // line did not compile even though the value was right there on the wire.
      price_label?: string | null;
      total_price: number;
    }>;
    lower_arch: Array<{
      id: string;
      name: string | { en?: string; it?: string; al?: string };
      description: string | { en?: string; it?: string; al?: string } | null;
      // i18n object in practice, not a string: the resource ships the category's
      // whole name map. Typed as both because legacy rows can carry a bare string.
      category: string | { en?: string; it?: string; al?: string; es?: string } | null;
      position?: string | null;
      // FDI teeth this line treats (EKDent 260905). Printed in place of the
      // arch by designs that opt in; always sent.
      teeth?: number[];
      /** ArtDent 260924 — treatment phase; null on a phase-less quote. */
      phase?: number | null;
      /** Phase 89 — the doctor for this line; omit where the flag is off. */
      doctor_id?: number | null;
      quantity: number;
      unit_price: number;
      // Free-text cost label ("Included", "Gratis") printed INSTEAD of the
      // amount when set. Sent by QuotePublicController for ALL THREE groups —
      // it was declared on `general` alone, so a design reading it off an arch
      // line did not compile even though the value was right there on the wire.
      price_label?: string | null;
      total_price: number;
    }>;
    // General (non-arch) services — Smile travel/extras plus any ad-hoc,
    // uncatalogued line added under quote_services_step_enabled. Included in
    // totals.subtotal by the backend, so every theme that renders a subtotal
    // MUST render these rows or the document will not foot.
    general?: Array<{
      id: string;
      name: string | { en?: string; it?: string; al?: string };
      description: string | { en?: string; it?: string; al?: string } | null;
      // i18n object in practice, not a string: the resource ships the category's
      // whole name map. Typed as both because legacy rows can carry a bare string.
      category: string | { en?: string; it?: string; al?: string; es?: string } | null;
      position?: string | null;
      // FDI teeth this line treats (EKDent 260905). Printed in place of the
      // arch by designs that opt in; always sent.
      teeth?: number[];
      /** ArtDent 260924 — treatment phase; null on a phase-less quote. */
      phase?: number | null;
      /** Phase 89 — the doctor for this line; omit where the flag is off. */
      doctor_id?: number | null;
      quantity: number;
      unit_price: number;
      // Free-text cost label ("Gratis", "Rimborso") printed INSTEAD of the
      // amount when set — the backend sends it, see QuotePublicController.
      price_label?: string | null;
      total_price: number;
    }>;
  };
  totals: {
    upper_arch: number;
    /** ArtDent 260924 — per-phase subtotals in phase order; [] when not phased. */
    phases?: { phase: number; subtotal: number }[];
    lower_arch: number;
    // General (non-arch) services total. Optional: already inside `subtotal`,
    // and absent from payloads served before it was exposed.
    general?: number;
    subtotal: number;
    discount_percent: number;
    discount_amount: number;
    /** Flight amount and direction, already applied to `total`. */
    flight_credit_amount?: number;
    flight_credit_direction?: FlightCreditDirection;
    /** Informational pair — NOT part of `total`. The amount is stored, not derived. */
    tax_credit_percent?: number | null;
    tax_credit_amount?: number | null;
    total: number;
  };
  payment: {
    /**
     * Does this document show a payment schedule at all?
     *
     * False only where a clinic agrees the schedule AFTER the patient accepts
     * (`quote_schedule_after_accept_enabled`, ArtDent 260914) and the quote has
     * not been accepted yet; the server zeroes the figures below to match.
     * OPTIONAL, and designs must read it as `!== false` — a payload from an
     * older backend omits it, and treating that absence as "hide" would blank
     * the schedule on every clinic that never asked for this.
     */
    schedule_visible?: boolean;
    deposit: { percent: number; amount: number };
    visits?: Array<{ label: string; days: number; percent: number; amount: number }>;
    first_visit: { percent: number; amount: number };
    second_visit: { percent: number; amount: number };
  };
  comparison: {
    uk_price: number;
    eu_price: number;
    savings: number;
  };
  journey: {
    visits?: Array<{ label: string; days: number; percent: number; amount: number }>;
    first_visit_days: number;
    second_visit_days: number;
  };
  vip: {
    included: boolean;
  };
  notes: {
    treatment: string | null;
  };
  clinic: {
    phone: string;
  };
  // Phase 58 (REQ-58-04): per-clinic branding overlay on YKM-shaped defaults.
  // Raw i18n objects preserved — frontend collapses to lang at render via t2().
  clinic_branding: {
    name: string;
    // Public quote visual theme. 'clinical-calm' = Navident redesign;
    // 'clinical-blue' = Dential redesign; anything else (incl. undefined)
    // = legacy layout.
    theme?: string;
    // Wordmark image rendered instead of the letter-mark + name when set
    // (clinical-blue). Path under frontend/public, e.g. /images/dential/logo.webp.
    logo?: string;
    tagline: LangString;
    // Clinic-specific cover paragraph (clinical-blue); falls back to the
    // generic coverSubtitle i18n key when absent.
    cover_subtitle?: LangString;
    // "About the clinic" story block (clinical-blue page 3); page section
    // is skipped entirely when heading is absent.
    about?: {
      heading: LangString;
      lead?: LangString;
      body?: LangString;
    };
    palette: {
      primary: string;
      primary_light: string;
      secondary: string;
      accent: string;
      accent_light: string;
    };
    photos: {
      waiting_area_logo: string;
      treatment_room_1: string;
      treatment_room_2: string;
      corridor: string;
      reception: string;
      consultation_office: string;
    };
    doctors: Array<{
      name: string;
      title: LangString;
      badge_key: string;
      // Free-text role chip (e.g. "Founder · Medical Director"); takes
      // precedence over badge_key when set (clinical-blue).
      badge?: LangString;
      photo: string;
      credentials: Array<{ icon: string; text: LangString }>;
      languages_key: string;
      accent_position: string;
    }>;
    testimonials: Array<{
      author?: string;
      rating?: string;
      meta: LangString;
      text: LangString;
    }>;
    tech_features: Array<{
      icon: string;
      name: LangString;
      description: LangString;
    }>;
    guarantees: Array<{
      icon: string;
      title: LangString;
      description: LangString;
      featured?: boolean;
    }>;
    vip_services: Array<{
      icon: string;
      title: LangString;
      description: LangString;
      badge: LangString;
    }>;
    vip_journey?: Record<string, unknown>;
    price_comparison: {
      uk_multiplier: number;
      eu_multiplier: number;
      examples: Array<{
        treatment_label: LangString;
        uk_range: string;
        eu_range: string;
        clinic_price: string;
      }>;
    };
    stats: {
      years_experience: string;
      happy_patients: string;
      google_rating: string;
      // Dentour theme: "% saved vs home country" cover stat (dentour.al states
      // no founding year / patient count, so its cover leads with this).
      savings_up_to?: string;
      // Optional label overrides for the two cover stat boxes (clinical-blue:
      // "Serving patients since" / "Healthy smiles delivered"). Fall back to
      // the yearsExperience / happyPatients i18n keys when absent.
      years_label?: LangString;
      patients_label?: LangString;
    };
    vip_threshold: number;
    contact: {
      email: string;
      website: string;
      location: string;
    };
  };
  feature_flags?: {
    quote_simplified_layout_enabled?: boolean;
    line_tooth_numbers_enabled?: boolean;
  };
}

// Get public quote data (no auth required)
export const getPublicQuote = async (token: string) => {
  const response = await api.get<PublicQuoteData>(`/quote/${token}`);
  return response.data;
};

// ---- Send via email / WhatsApp (mirrors the Phase 66 invoice send API) ----

/** POST /quotes/{id}/send-email body. */
export interface SendQuoteEmailPayload {
  to_email: string;
  subject: string;
  body_text: string;
}

/** POST /quotes/{id}/send-whatsapp body. to_whatsapp = lead.phone. */
export interface SendQuoteWhatsAppPayload {
  to_whatsapp: string;
  body_text: string;
  /** Sending WhatsApp account. Omit to use the sender's own (Zaffiro 260813). */
  account_id?: string;
}

/**
 * Server-resolved template defaults for the Send Email dialog. Tokens
 * ({quoteNumber}, {leadFirstName}, {clinicName}, {totalAmount}, {quoteUrl})
 * are substituted server-side; the operator sees fully-resolved text.
 */
export const previewQuoteEmail = async (id: string): Promise<SendEmailPreview> => {
  const response = await api.get<SendEmailPreview>(`/quotes/${id}/send-email/preview`);
  return response.data;
};

/** Server-resolved template defaults for the Send WhatsApp dialog. */
export const previewQuoteWhatsApp = async (id: string): Promise<SendWhatsAppPreview> => {
  const response = await api.get<SendWhatsAppPreview>(`/quotes/${id}/send-whatsapp/preview`);
  return response.data;
};

/**
 * Queue the email send (202 { queued: true }). The Mailable runs on the queue
 * worker with the quote PDF attached; email has no delivery polling.
 */
export const sendQuoteEmail = async (
  id: string,
  payload: SendQuoteEmailPayload,
): Promise<{ queued: true }> => {
  const response = await api.post<{ queued: true }>(`/quotes/${id}/send-email`, payload);
  return response.data;
};

/**
 * Queue the WhatsApp send (202 { queued, whatsapp_message_id }). The dialog
 * polls /whatsapp/messages/{id} for delivery status post-dispatch.
 */
export const sendQuoteWhatsApp = async (
  id: string,
  payload: SendQuoteWhatsAppPayload,
): Promise<{ queued: true; whatsapp_message_id: string }> => {
  const response = await api.post<{ queued: true; whatsapp_message_id: string }>(
    `/quotes/${id}/send-whatsapp`,
    payload,
  );
  return response.data;
};
